import { useForm, Controller } from "react-hook-form";
import { useEffect, useState, useRef } from "react";
import { createTask, updateTask } from "../../api/task-api"; 
import { getStaff } from "../../api/staff-calls-api";
import { Dialog } from "@headlessui/react";
import SelectDropdown from "../../components/listbox";
import { X, FileText, FileSpreadsheet, FileImage, File, Trash2, ClipboardList, CloudUpload, CheckCircle, Target, Folder } from "lucide-react";
import UploadDocumentModal from "../../components/UploadDocumentModal";
import { jwtDecode } from "jwt-decode";
import Select from "react-select";

const priorityOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
];

const statusOptions = [
    { value: "pending", label: "Pending" }, 
    { value: "in_progress", label: "In Progress" }, 
    { value: "completed", label: "Completed" }, 
    { value: "planned", label: "Planned" },
    { value: "cancelled", label: "Cancelled" },
];

/* File Display Component - Same as TaskReportModal */
const FileItem = ({ file, onRemove }) => {
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemove = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsRemoving(true);
    await onRemove(file);
  };

  const getFileTypeColor = (fileName) => {
    if (!fileName || typeof fileName !== 'string') {
      return 'text-gray-600 bg-gray-50';
    }
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'text-red-600 bg-red-50';
      case 'docx': case 'doc': return 'text-blue-600 bg-blue-50';
      case 'xlsx': case 'xls': return 'text-green-600 bg-green-50';
      case 'txt': return 'text-gray-600 bg-gray-50';
      case 'jpg': case 'jpeg': case 'png': case 'gif': return 'text-purple-600 bg-purple-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition-all duration-300">
      <div className="flex items-center p-4 space-x-4">
        <div className={`flex-shrink-0 p-3 rounded-lg transition-colors ${getFileTypeColor(file.originalName)}`}>
          <FileText className="w-6 h-6" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate pr-2">
            {file.originalName}
          </p>
          <div className="flex items-center space-x-3 mt-1">
            <span className="text-xs font-medium text-green-600">Ready</span>
          </div>
        </div>

        <button
          type="button"
          onClick={handleRemove}
          disabled={isRemoving}
          className={`p-2 rounded-lg transition-all duration-200 ${
            isRemoving
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100'
          }`}
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

const AddTaskModal = ({ isOpen, onClose, activityId, onTaskCreated, task }) => { 
    const { register, handleSubmit, reset, setValue, control } = useForm();
    const [docTab, setDocTab] = useState("existing");
    const [selectedPriority, setSelectedPriority] = useState(priorityOptions[0]);
    const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]); 
    
    // TaskReportModal upload pattern
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [documentPickerOpen, setDocumentPickerOpen] = useState(false);
    const [uploadedDocuments, setUploadedDocuments] = useState([]);
    
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [staffOptions, setStaffOptions] = useState([]);

    const formRef = useRef(null);
    const messagesRef = useRef(null);
    const isEditing = !!task; 

    const token = localStorage.getItem("token");
    const decoded = token ? jwtDecode(token) : null;
    const staffId = decoded ? decoded.id : null;

    // Scroll to top when messages change
    useEffect(() => {
        if (errorMsg || successMsg) {
            if (formRef.current) {
                formRef.current.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    }, [errorMsg, successMsg]);

    useEffect(() => {
        if (isOpen) {
            const fetchStaff = async () => {
                try {
                    const staffResponse = await getStaff();
                    if (staffResponse && staffResponse.list && staffResponse.list.length > 0) {
                        const options = staffResponse.list.map(s => ({
                            value: s._id,
                            label: `${s.firstName || ''} ${s.lastName || ''}`.trim() 
                        }));
                        setStaffOptions(options);

                        if (isEditing && task && task.assignedTo) {
                            const defaultAssignee = options.find(s => s.value === task.assignedTo._id);
                            if (defaultAssignee) {
                                setValue("assignedTo", defaultAssignee);
                            }
                        }
                    } else {
                        console.warn("Staff response list is empty or unexpected format.");
                        setStaffOptions([]); 
                        setErrorMsg("No staff members found or failed to parse staff list.");
                    }
                } catch (error) {
                    console.error("Failed to fetch staff members:", error);
                    setErrorMsg("Failed to load staff list. Please check network or API.");
                }
            };
            fetchStaff();
        }
    }, [isOpen, isEditing, task, setValue]);

    useEffect(() => {
        if (isOpen) {
            if (!isEditing) {
                setValue("title", "");
                setValue("description", "");
                setValue("startDate", "");
                setValue("dueDate", "");
                setValue("assignedTo", null); 
                setSelectedPriority(priorityOptions[0]);
                setSelectedStatus(statusOptions[0]); 
                setUploadedDocuments([]);
                setDocTab("existing");
            } else if (task) {
                setValue("title", task.title || "");
                setValue("description", task.description || "");
                setValue("startDate", task.startDate ? new Date(task.startDate).toISOString().split('T')[0] : "");
                setValue("dueDate", task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : "");
                
                const currentPriority = priorityOptions.find(opt => opt.value === task.priority) || priorityOptions[0];
                setSelectedPriority(currentPriority);

                const currentStatus = statusOptions.find(opt => opt.value === task.status) || statusOptions[0];
                setSelectedStatus(currentStatus);

                // Set uploaded documents from task
                setUploadedDocuments(task.documents || []);
                setValue("documentIds", task.documentIds || []);
            }
            setErrorMsg("");
            setSuccessMsg("");
        } else {
            reset(); 
            setSelectedPriority(priorityOptions[0]);
            setSelectedStatus(statusOptions[0]);
            setUploadedDocuments([]);
            setDocTab("existing");
            setSubmitting(false);
            setErrorMsg("");
            setSuccessMsg("");
        }
    }, [isOpen, task, isEditing, reset, setValue]);

    useEffect(() => {
        setValue("priority", selectedPriority.value);
    }, [selectedPriority, setValue]);

    useEffect(() => {
        setValue("status", selectedStatus.value); 
    }, [selectedStatus, setValue]);

    // Set document IDs when uploaded documents change
    useEffect(() => {
        const documentIds = uploadedDocuments.map(doc => doc._id).filter(Boolean);
        setValue("documentIds", documentIds);
    }, [uploadedDocuments, setValue]);

    // TaskReportModal upload success pattern
    const handleUploadSuccess = (files) => {
        console.log("Upload success received:", files);
        setUploadedDocuments(files);
        setIsUploadModalOpen(false);
    };

    // TaskReportModal document selection pattern
    const handleSelectedDocs = (docIds, docObjects) => {
        setUploadedDocuments(docObjects);
        setValue("documentIds", docIds);
    };

    // TaskReportModal remove pattern
    const handleRemoveFile = async (file) => {
        if (submitting) return;
        setUploadedDocuments(prev => prev.filter(f => f !== file));
    };

    // Upload button click handler
    const handleUploadButtonClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log("Upload button clicked, opening modal...");
        setIsUploadModalOpen(true);
    };

    const onSubmit = async (data) => {
        setSubmitting(true);
        setErrorMsg(""); 
        setSuccessMsg(""); 
        try {
            const assignedToValue = data.assignedTo ? data.assignedTo.value : null;

            let taskActivity;
            if (isEditing) {
                taskActivity = task.activity ? task.activity._id : null;
            } else {
                taskActivity = activityId || null; 
            }

            if (!taskActivity) {
                setErrorMsg("An activity must be selected for this task.");
                setSubmitting(false);
                return; 
            }

            const payload = {
                ...data,
                assignedTo: assignedToValue,
                activity: taskActivity,
                status: selectedStatus.value
            };

            console.log("Task Payload (before API call):", payload);

            let response;
            if (isEditing) {
                response = await updateTask(task._id, payload); 
                setSuccessMsg("Task updated successfully!");
            } else {
                response = await createTask(payload);
                setSuccessMsg("Task created successfully!");
            }

            if (response && response.success) {
                setTimeout(() => {
                    onClose();
                    onTaskCreated(response.data); 
                }, 2000);
            } else {
                throw new Error(response?.message || "Task operation failed");
            }
        } catch (err) {
            console.error("Task creation/update failed:", err);
            console.error("Backend Error Response:", err.response?.data); 
            setErrorMsg(
                err.response?.data?.message || err.message || "Failed to save task. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" aria-hidden="true" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl transform transition-all duration-300 ease-out max-h-[95vh] overflow-hidden">
                    {/* Header with Orange Theme */}
                    <div className="bg-gradient-to-r from-orange-300 to-orange-400 rounded-t-2xl p-4 sm:p-6 border-b border-orange-500">
                        <div className="flex justify-between items-center">
                            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                                    <ClipboardList className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
                                </div>
                                <div>
                                    <h2 className="text-lg sm:text-xl font-bold text-black">
                                        {isEditing ? "Edit Task" : "Add Task"}
                                    </h2>
                                    <div className="flex items-center mt-1 text-black text-sm">
                                        <Folder className="w-3 h-3 mr-1" />
                                        <span>Programs Department</span>
                                    </div>
                                </div>
                            </div>
                            <button 
                                onClick={onClose}
                                className="p-2 rounded-lg transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Form Body */}
                    <div className="p-4 sm:p-6 overflow-y-auto max-h-[80vh]">
                        <form
                            ref={formRef}
                            onSubmit={handleSubmit(onSubmit)}
                            className="space-y-6"
                        >
                            <div ref={messagesRef}>
                                {errorMsg && (
                                    <div className="bg-red-50 text-red-700 p-3 rounded-lg flex items-center mb-4">
                                        <i className="fas fa-exclamation-circle mr-2"></i>
                                        <p className="text-sm font-medium">{errorMsg}</p>
                                    </div>
                                )}
                                {successMsg && (
                                    <div className="bg-green-50 text-green-700 p-3 rounded-lg flex items-center mb-4">
                                        <i className="fas fa-check-circle mr-2"></i>
                                        <p className="text-sm font-medium">{successMsg}</p>
                                    </div>
                                )}
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Task Title <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Target className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        {...register("title", { required: true })}
                                        className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                        placeholder="Enter task title"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description 
                                </label>
                                <div className="relative">
                                    <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                                        <FileText className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <textarea
                                        {...register("description")}
                                        rows={3}
                                        className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 resize-none"
                                        placeholder="Describe the task details, objectives, and any specific requirements"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Assignee <span className="text-red-500">*</span>
                                </label>
                                <Controller
                                    name="assignedTo"
                                    control={control}
                                    rules={{ required: false }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={staffOptions}
                                            isClearable
                                            placeholder="Select team member to assign this task"
                                            className="rounded-lg"
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    borderRadius: '0.75rem',
                                                    minHeight: '44px',
                                                    borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                                                    boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                                    '&:hover': {
                                                        borderColor: '#F97316'
                                                    }
                                                }),
                                                option: (base, state) => ({
                                                    ...base,
                                                    backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                                    color: state.isSelected ? 'white' : '#1F2937',
                                                    '&:hover': {
                                                        backgroundColor: '#FFEDD5'
                                                    }
                                                }),
                                            }}
                                        />
                                    )}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Start Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        {...register("startDate")}
                                        className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Due Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        {...register("dueDate")}
                                        className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Status
                                    </label>
                                    <Select
                                        value={selectedStatus}
                                        onChange={setSelectedStatus}
                                        options={statusOptions}
                                        className="w-full rounded-lg"
                                        placeholder="Select Status"
                                        styles={{
                                            control: (base, state) => ({
                                                ...base,
                                                borderRadius: '0.75rem',
                                                minHeight: '44px',
                                                borderColor: state.isFocused ? '#EA580C' : '#D1D5DB',
                                                boxShadow: state.isFocused ? '0 0 0 1px #EA580C' : 'none',
                                                '&:hover': {
                                                    borderColor: '#EA580C'
                                                }
                                            }),
                                            option: (base, state) => ({
                                                ...base,
                                                backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                                color: state.isSelected ? 'white' : '#1F2937',
                                                '&:hover': {
                                                    backgroundColor: '#FFEDD5'
                                                }
                                            }),
                                        }}
                                    />
                                    <input type="hidden" {...register("status")} value={selectedStatus.value} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Priority
                                    </label>
                                    <SelectDropdown
                                        options={priorityOptions}
                                        selected={selectedPriority}
                                        onChange={setSelectedPriority}
                                        position="above"
                                    />
                                    <input type="hidden" {...register("priority")} value={selectedPriority.value} />
                                </div>
                            </div>

                            {/* TaskReportModal-style document section */}
                            <div className="border rounded-lg p-4 bg-gray-50 space-y-4">
                                <h3 className="text-lg font-semibold text-gray-700">Supporting Documents</h3>
                                <button
                                    type="button"
                                    onClick={handleUploadButtonClick}
                                    disabled={submitting}
                                    className="w-full border-2 border-dashed border-gray-300 rounded-md p-6 text-center text-gray-500 hover:border-orange-500 hover:text-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <CloudUpload className="mx-auto h-8 w-8 mb-2" />
                                    Click to Upload Documents
                                </button>

                                {/* Display uploaded files */}
                                {uploadedDocuments.length > 0 && (
                                    <div className="space-y-2">
                                        <h4 className="text-sm font-medium text-gray-800">
                                            Uploaded Files ({uploadedDocuments.length})
                                        </h4>
                                        <div className="space-y-2">
                                            {uploadedDocuments.map((file, index) => (
                                                <FileItem
                                                    key={`${file.originalName}-${index}`}
                                                    file={file}
                                                    onRemove={handleRemoveFile}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    disabled={submitting}
                                    className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium order-2 sm:order-1"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-5 py-2.5 bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg text-white hover:from-orange-500 hover:to-orange-600 transition-colors font-medium shadow-sm disabled:bg-orange-400 disabled:cursor-not-allowed flex items-center justify-center order-1 sm:order-2"
                                >
                                    {submitting ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4 text-white mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {isEditing ? "Updating..." : "Creating..."}
                                        </>
                                    ) : (
                                        <>
                                            <i className={`fas ${isEditing ? 'fa-save' : 'fa-plus'} mr-2`}></i>
                                            {isEditing ? "Update Task" : "Create Task"}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* TaskReportModal-style upload modal */}
                    <UploadDocumentModal
                        isOpen={isUploadModalOpen}
                        onClose={() => {
                            console.log("Closing upload modal...");
                            setIsUploadModalOpen(false);
                        }}
                        staffId={staffId}
                        onUploadSuccessWithFiles={handleUploadSuccess}
                        category="Task Documents"
                    />
                </Dialog.Panel>
            </div>
        </Dialog>
    );
};

export default AddTaskModal;